import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Grid,
  CardHeader,
  CardContent,
  Card,
  Box,
  TextField,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Typography,
  Paper,
  Divider,
  Chip,
  Fade,
  Zoom,
  IconButton,
  InputAdornment,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { userServices } from "../../services/AllServices";
import { EMAIL_REGEX, IMAGE_BASE } from "../../utils/constants";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { userSchema } from "../../utils/Schema";
import {
  PersonAdd as PersonAddIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  Security as SecurityIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Visibility,
  VisibilityOff,
  Close as CloseIcon,
} from "@mui/icons-material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

function AddEditUser() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);


  // Image state and ref
  const [imagePreview, setImagePreview] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Driver license image state and ref
  const [licensePreview, setLicensePreview] = useState("");
  const [licenseFile, setLicenseFile] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const licenseInputRef = useRef(null);
  const [licenseDeleted, setLicenseDeleted] = useState(false);

  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData("text");
    if (/^\d/.test(pastedText)) {
      e.preventDefault();
    }
  };

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await userServices.getById({ id });
        setData(response?.data?.data);

        // If userImage exists, set preview
        if (response?.data?.data?.userImage) {
          setImagePreview(response.data.data.userImage);
        }

        // If driverLicenseImage exists, set preview
        if (response?.data?.data?.driverLicenseImage) {
          setLicensePreview(response.data.data.driverLicenseImage);
        setLicenseDeleted(false);
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageLoading(true);
      setImageFile(file);

      // Preview as base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setImageLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };
  console.log("data", licenseFile);
  

  const handleLicenseImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLicenseLoading(true);
      setLicenseFile(file);
      setLicenseDeleted(false);

      // Preview as base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setLicensePreview(reader.result);
        setLicenseLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLicense = () => {
    setLicenseFile(null);
    setLicensePreview("");
    setLicenseDeleted(true);
    // Reset the file input
    if (licenseInputRef.current) {
      licenseInputRef.current.value = "";
    }
  };

  const handleFinalSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();

      formData.append("id", id || "");
      formData.append("firstName", values.firstName.trim());
      formData.append("lastName", values.lastName.trim());
      formData.append("email", values.email);
      formData.append("phoneNumber", values.phoneNumber);
      if (values.dob) {
        const dobDate = values.dob.includes("T")
          ? values.dob.split("T")[0]
          : values.dob;
        formData.append("dob", dobDate);
      }
      formData.append("driverLicence", values.driverLicence);
      formData.append("gender", values.gender);

      if (values.password) {
        formData.append("password", values.password);
      }
      formData.append("status", "1");

      // Address fields
      formData.append("address", values.street.trim());
      formData.append("city", values.addressCity.trim());
      formData.append("province", values.province.trim());
      formData.append("country", values.country.trim());
      formData.append("countryCode", values.countryCode.trim());
      formData.append("postalCode", values.postalCode.trim());

      // Map role back to API format
      const apiRole =
        values.role === "consumer"
          ? "consumer"
          : values.role === "Dealer"
            ? "dealer"
            : values.role;
      formData.append("role", apiRole);

      // Dealer only fields
      if (values.role === "Dealer") {
        formData.append("businessName", values.businessName.trim());
        formData.append("dealershipNumber", values.dealershipNumber.trim());
        formData.append("taxNumber", values.taxNumber.trim());
      }

      // Append image file if present
      if (imageFile) {
        formData.append("userImage", imageFile);
      }

      if (licenseFile) {
        // New file uploaded in both add and edit
        formData.append("driverLicenseImage", licenseFile);
      } else if (id) {
        // Edit mode: only send null if user explicitly deleted existing image
        if (licenseDeleted) {
          formData.append("driverLicenseImage", null);
        }
        // else: do not append the key so backend keeps existing image
      } else {
        // Add mode with no upload -> preserve existing behavior (send null)
        formData.append("driverLicenseImage", null);
      }

      // Call API
      const response = id
        ? await userServices.edit(formData)
        : await userServices.add(formData);

      toast.success(response?.data?.message, { toastId: "success" });
      navigate(-1);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: userSchema(id),
    initialValues: {
      firstName: data?.firstName || "",
      lastName: data?.lastName || "",
      email: data?.email || "",
      phoneNumber: data?.phoneNumber || "",
      countryCode: data?.countryCode || "",
      dob: data?.dob ? data.dob.split("T")[0] : "",
      gender: data?.gender || "",
      driverLicence: data?.driverLicence || "",
      driverLicenseImage: data?.driverLicenseImage || "",
      role:
        data?.role === "consumer"
          ? "consumer"
          : data?.role === "dealer"
            ? "Dealer"
            : data?.role || "consumer",
      password: "",
      street: data?.address || "",
      addressCity: data?.city || "",
      province: data?.province || "",
      country: data?.country || "",
      postalCode: data?.postalCode || "",
      businessName: data?.businessName || "",
      dealershipNumber: data?.dealershipNumber || "",
      taxNumber: data?.taxNumber || "",
    },
    validate: (values) => {
      const errors = {};
      const email = values.email.trim();

      if (!email) {
        errors.email = "This field is required";
      } else if (!EMAIL_REGEX.test(email)) {
        errors.email = "Invalid email address";
      }
      return errors;
    },
    onSubmit: async (values) => {
      await handleFinalSubmit(values);
    },
  });

  const { values, touched, errors, handleChange, handleSubmit, setFieldValue, setFieldError } =
    formik;

  const handleEmailChange = (event) => {
    const { value } = event.target;
    const trimmedValue = value.replace(/^\s+|\s+(?=\s)|\s+$/g, "");
    formik.setFieldValue("email", trimmedValue);
  };

  // -- Section Fragments to support conditional ordering --
  const personalInfoSection = (
    <Box className="section-wrapper">
      <Card elevation={1} className="section-card">
        <CardHeader
          title={
            <Box className="section-header">
              <PersonAddIcon color="primary" />
              <Typography variant="h5" className="section-title">
                Personal Information
              </Typography>
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                required
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                onPaste={handlePaste}
                error={Boolean(touched.firstName && errors.firstName)}
                helperText={touched.firstName && errors.firstName}
                inputProps={{ maxLength: 20 }}
                className="form-field"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                required
                name="lastName"
                value={values.lastName}
                onChange={handleChange}
                onPaste={handlePaste}
                error={Boolean(touched.lastName && errors.lastName)}
                helperText={touched.lastName && errors.lastName}
                inputProps={{ maxLength: 20 }}
                className="form-field"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Email Address"
                required
                name="email"
                value={formik.values.email}
                onChange={handleEmailChange}
                error={Boolean(
                  formik.touched.email && formik.errors.email
                )}
                helperText={formik.touched.email && formik.errors.email}
                inputProps={{ maxLength: 50 }}
                className="form-field"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
              
                <PhoneInput
                  country={'us'}
                  value={formik.values.countryCode + formik.values.phoneNumber}
                  onChange={(value, country) => {
                    const dialCode = country?.dialCode || "";
                    const iso2 = country?.countryCode?.toUpperCase() || "";
  
                    formik.setFieldValue("countryCode", "+" + dialCode);
                    formik.setFieldValue("countryIsoCode", iso2);
                    formik.setFieldValue("phoneNumber", value.replace(dialCode, ""));
                  }}
                  onBlur={() => formik.setFieldTouched('phoneNumber', true)}
                  inputStyle={{
                    width: '100%',
                    height: '56px',
                    fontSize: '16px',
                    border: touched.phoneNumber && errors.phoneNumber 
                      ? '1px solid #d32f2f' 
                      : '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: '4px',
                  }}
                  containerStyle={{
                    width: '100%',
                  }}
                  buttonStyle={{
                    border: touched.phoneNumber && errors.phoneNumber 
                      ? '1px solid #d32f2f' 
                      : '1px solid rgba(0, 0, 0, 0.23)',
                    borderRight: 'none',
                    borderRadius: '4px 0 0 4px',
                  }}
                  dropdownStyle={{
                    maxHeight: '200px',
                  }}
                  enableSearch
                  searchPlaceholder="Search country"
                  preferredCountries={['us', 'ca', 'gb']}
                />
                {touched.phoneNumber && errors.phoneNumber && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.75, display: 'block' }}
                  >
                    {errors.phoneNumber}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Date of Birth"
                  value={values.dob ? dayjs(values.dob) : null}
                  onChange={(newValue) => {
                    if (newValue && dayjs(newValue).isValid()) {
                      // Check if date is in valid range
                      const today = dayjs();
                      const minDate = dayjs().subtract(18, 'year');
                      const maxDate = today;
                      
                      if (newValue.isAfter(maxDate)) {
                        setFieldError("dob", "Date of birth cannot be in the future");
                        return;
                      }
                      
                      if (newValue.isAfter(minDate)) {
                        setFieldError("dob", "You must be at least 18 years old");
                        return;
                      }
                      
                      // Format as YYYY-MM-DD to avoid timezone issues
                      const formattedDate = newValue.format("YYYY-MM-DD");
                      setFieldValue("dob", formattedDate);
                      // Clear any existing errors
                      if (errors.dob) {
                        setFieldError("dob", "");
                      }
                    } else {
                      setFieldValue("dob", "");
                      setFieldError("dob", "Please enter a valid date");
                    }
                  }}
                  onError={(error) => {
                    if (error) {
                      setFieldError("dob", "Please enter a valid date");
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      name: "dob",
                      error: Boolean(touched.dob && errors.dob),
                      helperText: touched.dob && errors.dob,
                      className: "form-field",
                      placeholder: "DD/MM/YYYY",
                    },
                  }}
                  format="DD/MM/YYYY"
                  disableFuture
                  maxDate={dayjs().subtract(18, 'year')} // Minimum age 18
                />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    placeholder="License number"
                    name="driverLicence"
                    value={values.driverLicence}
                    onChange={handleChange}
                    onPaste={handlePaste}
                    error={Boolean(
                      touched.driverLicence && errors.driverLicence
                    )}
                    helperText={
                      touched.driverLicence && errors.driverLicence
                    }
                    inputProps={{ maxLength: 50 }}
                    sx={{ flex: 1 }}
                  />
                  <input
                    accept="image/*,.pdf"
                    type="file"
                    ref={licenseInputRef}
                    onChange={handleLicenseImageChange}
                    style={{ display: "none" }}
                  />
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => licenseInputRef.current.click()}
                    sx={{ 
                      minWidth: '100px',
                      height: '56px',
                      borderRadius: '4px'
                    }}
                  >
                    {licenseLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : licensePreview ? (
                      "Change"
                    ) : (
                      "Upload"
                    )}
                  </Button>
                </Box>
                {console.log("licensePreview", licensePreview)
                }
                {licensePreview.length > 0 && (
                  <Box sx={{ 
                    mt: 1, 
                    p: 1, 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1, 
                    backgroundColor: '#f9f9f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      Document uploaded: {licenseFile?.name || 'license-document'}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleRemoveLicense}
                      sx={{ 
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'error.light',
                          color: 'error.contrastText'
                        }
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="Gender"
                required
                name="gender"
                value={values.gender}
                onChange={handleChange}
                onPaste={handlePaste}
                error={Boolean(touched.gender && errors.gender)}
                helperText={touched.gender && errors.gender}
                className="form-field"
              >
                <MenuItem value="">Select Gender</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid> */}

            {!id && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  required
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  error={Boolean(touched.password && errors.password)}
                  helperText={touched.password && errors.password}
                  inputProps={{ maxLength: 100 }}
                  className="form-field"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  const addressSection = (
    <Box className="section-wrapper">
      <Card elevation={1} className="section-card">
        <CardHeader
          title={
            <Box className="section-header">
              <HomeIcon color="primary" />
              <Typography variant="h5" className="section-title">
                Address Information
              </Typography>
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Street Address"
                required
                name="street"
                value={values.street}
                onChange={handleChange}
                error={Boolean(touched.street && errors.street)}
                helperText={touched.street && errors.street}
                inputProps={{ maxLength: 100 }}
                className="form-field"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="City"
                required
                name="addressCity"
                value={values.addressCity}
                onChange={handleChange}
                error={Boolean(
                  touched.addressCity && errors.addressCity
                )}
                helperText={touched.addressCity && errors.addressCity}
                inputProps={{ maxLength: 50 }}
                className="form-field"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Province/State"
                required
                name="province"
                value={values.province}
                onChange={handleChange}
                error={Boolean(touched.province && errors.province)}
                helperText={touched.province && errors.province}
                inputProps={{ maxLength: 50 }}
                className="form-field"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Country"
                required
                name="country"
                value={values.country}
                onChange={handleChange}
                error={Boolean(touched.country && errors.country)}
                helperText={touched.country && errors.country}
                inputProps={{ maxLength: 50 }}
                className="form-field"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Postal/ZIP Code"
                required
                name="postalCode"
                value={values.postalCode}
                onChange={handleChange}
                error={Boolean(touched.postalCode && errors.postalCode)}
                helperText={touched.postalCode && errors.postalCode}
                inputProps={{ maxLength: 20 }}
                className="form-field"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  const dealerBusinessSection = (
    values.role === "Dealer" && (
      <Box className="section-wrapper">
        <Card elevation={1} className="section-card">
          <CardHeader
            title={
              <Box className="section-header">
                <BusinessIcon color="primary" />
                <Typography variant="h5" className="section-title">
                  Business Information
                </Typography>
              </Box>
            }
          />
          <CardContent>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Business Name"
                  required
                  name="businessName"
                  value={values.businessName}
                  onChange={handleChange}
                  error={Boolean(
                    touched.businessName && errors.businessName
                  )}
                  helperText={
                    touched.businessName && errors.businessName
                  }
                  inputProps={{ maxLength: 100 }}
                  className="form-field"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Dealership Number (Dealer ID)"
                  required
                  name="dealershipNumber"
                  value={values.dealershipNumber}
                  onChange={handleChange}
                  error={Boolean(
                    touched.dealershipNumber && errors.dealershipNumber
                  )}
                  helperText={
                    touched.dealershipNumber && errors.dealershipNumber
                  }
                  inputProps={{ maxLength: 50 }}
                  className="form-field"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="HST/GST Tax Number"
                  required
                  name="taxNumber"
                  value={values.taxNumber}
                  onChange={handleChange}
                  error={Boolean(touched.taxNumber && errors.taxNumber)}
                  helperText={touched.taxNumber && errors.taxNumber}
                  inputProps={{ maxLength: 50 }}
                  className="form-field"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    )
  );

  return (
    <Box className="addedit-user-container">
      <Box className="page-header-section">
        <Box className="page-header">
          <PersonAddIcon color="primary" />
          <Typography variant="h4" component="h1" className="page-title">
            {id ? "Edit User" : "Add New User"}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          className="page-subtitle"
        >
          {id
            ? "Update user information and settings"
            : "Create a new user account with complete profile information"}
        </Typography>
      </Box>

      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Box className="form-sections-container">
            {/* Profile Image Section */}
            <Box className="section-wrapper">
              <Paper elevation={0} className="upload-area-transparent">
                <input
                  accept="image/*"
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />

                <Box
                  onClick={() => fileInputRef.current.click()}
                  style={{ cursor: "pointer" }}
                >
                  {imageLoading ? (
                    <Fade in={imageLoading}>
                      <Box className="loading-container">
                        <CircularProgress size={40} thickness={4} />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          className="loading-text"
                        >
                          Processing image...
                        </Typography>
                      </Box>
                    </Fade>
                  ) : (
                    <Zoom in={!imageLoading}>
                      <Box>
                        <Avatar
                          src={
                            imagePreview
                              ? imagePreview.startsWith("data:")
                                ? imagePreview
                                : IMAGE_BASE + imagePreview
                              : "/path/to/default-avatar.jpg"
                          }
                          alt="Profile Preview"
                          className="upload-avatar"
                        />
                        <Typography
                          variant="h6"
                          gutterBottom
                          className="upload-title"
                        >
                          {imagePreview
                            ? "Profile Photo"
                            : "Upload Profile Photo"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          className="upload-subtitle"
                        >
                          Click to {imagePreview ? "change" : "upload"} profile
                          image
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={
                            imagePreview ? <EditIcon /> : <CloudUploadIcon />
                          }
                          className="upload-button"
                        >
                          {imagePreview ? "Change Image" : "Upload Image"}
                        </Button>
                      </Box>
                    </Zoom>
                  )}
                </Box>

                {formik.touched.image && formik.errors.image && (
                  <Typography
                    variant="body2"
                    color="error"
                    className="error-message"
                  >
                    <CheckCircleIcon fontSize="small" />
                    {formik.errors.image}
                  </Typography>
                )}
              </Paper>
            </Box>

            {/* Account Type Section */}
            <Box className="section-wrapper">
              <Card elevation={1} className="account-type-card-transparent">
                <CardContent>
                  <Box className="account-type-header">
                    <SecurityIcon color="primary" />
                    <Typography variant="h6" className="account-type-title">
                      Account Type
                    </Typography>
                  </Box>
                  <FormControl
                    fullWidth
                    error={Boolean(touched.role && errors.role)}
                    required
                  >
                    <InputLabel id="role-label">Select Account Type</InputLabel>
                    <Select
                      labelId="role-label"
                      id="role"
                      name="role"
                      value={values.role}
                      label="Select Account Type"
                      onChange={(e) => setFieldValue("role", e.target.value)}
                      className="account-type-select"
                    >
                      <MenuItem value="consumer">
                        <Box className="account-type-menu-item">
                          <PersonAddIcon color="action" />
                          <Box>
                            <Typography
                              variant="body1"
                              className="account-type-menu-text"
                            >
                              Consumer
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Dealer">
                        <Box className="account-type-menu-item">
                          <PersonAddIcon color="action" />
                          <Box>
                            <Typography
                              variant="body1"
                              className="account-type-menu-text"
                            >
                              Dealer
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    </Select>
                    {touched.role && errors.role && (
                      <FormHelperText>{errors.role}</FormHelperText>
                    )}
                  </FormControl>
                </CardContent>
              </Card>
            </Box>

            {/* Conditional ordering of sections based on role */}
            {values.role === "Dealer" ? (
              <>
                {dealerBusinessSection}
                {addressSection}
                {personalInfoSection}
              </>
            ) : (
              <>
                {personalInfoSection}
                {addressSection}
              </>
            )}

            {/* Submit button */}
            <Box className="section-wrapper">
              <Card elevation={1} className="submit-section-transparent">
                <CardContent>
                  <Box className="submit-header">
                    <Typography variant="h6" className="submit-title">
                      Ready to {id ? "Update" : "Create"} User?
                    </Typography>
                  
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="submit-description"
                  >
                    {id
                      ? "Review the information above and click update to save your changes."
                      : "Please review all information before creating the user account. This action cannot be undone."}
                  </Typography>
                  <Box className="submit-buttons">
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate(-1)}
                      className="cancel-button"
                    >
                      Cancel
                    </Button>
                    <LoadingButton
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                      startIcon={id ? <EditIcon /> : <PersonAddIcon />}
                      className="submit-button"
                    >
                      {loading
                        ? "Processing..."
                        : `${id ? "Update" : "Create"} User`}
                    </LoadingButton>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Form>
      </FormikProvider>
    </Box>
  );
}

export default AddEditUser;
